import React, { useState } from "react";
import robotGif from "../images/robot.gif";

interface ChatbotProps {
  position?: "bottom-right" | "header";
  onNavigate?: (view: string) => void;
}

interface ChatMessage {
  id: string;
  type: "bot" | "user" | "help-options" | "shortcuts";
  content?: string;
  timestamp: Date;
}

interface HelpOption {
  id: string;
  title: string;
  description: string;
  view: string;
  icon: string;
  shortcut?: string;
}

const helpOptions: HelpOption[] = [
  {
    id: "jobcard",
    title: "Manage Job Cards",
    description: "Create, view, and track job cards for vehicle service and repair work. Keep track of all workshop activities.",
    view: "104",
    icon: "📋",
    shortcut: "F2",
  },
  {
    id: "customer",
    title: "Add New Customer",
    description: "Register new customers in the system. Store customer details, contact information, and service history.",
    view: "123",
    icon: "👤",
    shortcut: "F3",
  },
  {
    id: "vehicle",
    title: "Register Vehicle",
    description: "Add new vehicles to the system. Link vehicles to customers and track vehicle information.",
    view: "102",
    icon: "🚗",
    shortcut: "F4",
  },
  {
    id: "inspection",
    title: "Start Vehicle Inspection",
    description: "Perform vehicle inspections before service. Document vehicle condition and identify issues.",
    view: "107",
    icon: "🔍",
    shortcut: "F1",
  },
  {
    id: "products",
    title: "Manage Products",
    description: "Add and manage products in your inventory. Track product details, pricing, and stock levels.",
    view: "106",
    icon: "📦",
    shortcut: "F5",
  },
  {
    id: "labour",
    title: "Add Labour & Services",
    description: "Define labor charges and other services. Set up service types and pricing for your workshop.",
    view: "119",
    icon: "🔧",
    shortcut: "F6",
  },
  {
    id: "vendor",
    title: "Manage Vendors",
    description: "Add and manage vendor information. Keep track of suppliers and purchase details.",
    view: "128",
    icon: "🏢",
    shortcut: "F7",
  },
  {
    id: "inventory",
    title: "View Inventory",
    description: "Check your current inventory levels. Monitor stock, parts, and materials availability.",
    view: "inventory",
    icon: "📦",
  },
  {
    id: "sales",
    title: "Sales Management",
    description: "Manage sales orders, invoices, and quotations. Track your sales and customer transactions.",
    view: "sales-orders",
    icon: "💰",
  },
  {
    id: "reports",
    title: "View Reports",
    description: "Access various reports and analytics. Get insights into your business performance.",
    view: "reports",
    icon: "📈",
  },
];

const keyboardShortcuts = [
  { category: "Form Operations", shortcuts: [
    { key: "Ctrl + S", action: "Save form" },
    { key: "Ctrl + Shift + S", action: "Save & Close" },
  ]},
  { category: "Navigation", shortcuts: [
    { key: "Esc", action: "Close/Cancel" },
    { key: "Ctrl + R", action: "Refresh" },
  ]},
  { category: "Data Management", shortcuts: [
    { key: "Ctrl + A", action: "Add record" },
    { key: "Ctrl + E", action: "Edit record" },
    { key: "Ctrl + D", action: "Delete record" },
  ]},
  { category: "Workshop Operations", shortcuts: [
    { key: "F1", action: "Inspection" },
    { key: "F2", action: "Job Card List" },
    { key: "F3", action: "Add Customer" },
    { key: "F4", action: "Add Vehicle" },
    { key: "F5", action: "Add Products" },
    { key: "F6", action: "Add Labour/Others" },
    { key: "F7", action: "Add Vendor" },
  ]},
  { category: "Job Cards", shortcuts: [
    { key: "Ctrl + J", action: "Job Card List" },
    { key: "Ctrl + Shift + J", action: "New Job Card" },
  ]},
  { category: "Print & Export", shortcuts: [
    { key: "Ctrl + P", action: "Print/PDF" },
  ]},
  { category: "Appearance", shortcuts: [
    { key: "Ctrl + Shift + D", action: "Toggle Dark Mode" },
  ]},
];

export const Chatbot: React.FC<ChatbotProps> = ({ position = "bottom-right", onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "1",
      type: "bot",
      content: "Hello! 👋 I'm here to help you navigate the system. What would you like to do today?",
      timestamp: new Date(),
    },
    {
      id: "2",
      type: "help-options",
      timestamp: new Date(),
    },
  ]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const isHeaderPosition = position === "header";

  const handleHelpOptionClick = (option: HelpOption) => {
    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: "user",
      content: option.title,
      timestamp: new Date(),
    };

    // Add bot explanation
    const botExplanation: ChatMessage = {
      id: (Date.now() + 1).toString(),
      type: "bot",
      content: option.description,
      timestamp: new Date(),
    };

    // Add bot navigation message
    const botNavigate: ChatMessage = {
      id: (Date.now() + 2).toString(),
      type: "bot",
      content: `Taking you to ${option.title}...`,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage, botExplanation, botNavigate]);

    // Navigate after showing explanation
    setTimeout(() => {
      if (onNavigate) {
        onNavigate(option.view);
      }
      setIsOpen(false);
      // Reset messages after navigation
      setTimeout(() => {
        setMessages([
          {
            id: "1",
            type: "bot",
            content: "Hello! 👋 I'm here to help you navigate the system. What would you like to do today?",
            timestamp: new Date(),
          },
          {
            id: "2",
            type: "help-options",
            timestamp: new Date(),
          },
        ]);
        setShowShortcuts(false);
      }, 500);
    }, 1500);
  };

  const handleShowShortcuts = () => {
    setShowShortcuts(true);
    const shortcutsMessage: ChatMessage = {
      id: Date.now().toString(),
      type: "shortcuts",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, shortcutsMessage]);
  };

  const handleBackToHelp = () => {
    setShowShortcuts(false);
    setSearchQuery("");
    setMessages([
      {
        id: "1",
        type: "bot",
        content: "Hello! 👋 I'm here to help you navigate the system. What would you like to do today?",
        timestamp: new Date(),
      },
      {
        id: "2",
        type: "help-options",
        timestamp: new Date(),
      },
    ]);
  };

  // Filter help options based on search query
  const filteredHelpOptions = helpOptions.filter((option) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      option.title.toLowerCase().includes(query) ||
      option.description.toLowerCase().includes(query)
    );
  });

  return (
    <>
      {/* Chatbot Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setShowShortcuts(false);
            setSearchQuery("");
            setMessages([
              {
                id: "1",
                type: "bot",
                content: "Hello! 👋 I'm here to help you navigate the system. What would you like to do today?",
                timestamp: new Date(),
              },
              {
                id: "2",
                type: "help-options",
                timestamp: new Date(),
              },
            ]);
          }
        }}
        className={`${
          isHeaderPosition
            ? "relative w-10 h-10 rounded-lg bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 border-none shadow-none"
            : "fixed bottom-6 right-6 z-[10001] w-16 h-16 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl border-2 border-primary-500 dark:border-primary-400"
        } transition-all duration-300 hover:scale-110 flex items-center justify-center cursor-pointer group`}
        aria-label="Open chatbot"
      >
        <img
          src={robotGif}
          alt="Chatbot"
          className={`${isHeaderPosition ? "w-8 h-8" : "w-12 h-12"} object-contain`}
        />
        {/* Pulse animation ring - only for bottom-right position */}
        {!isHeaderPosition && (
          <span className="absolute inset-0 rounded-full bg-primary-500 dark:bg-primary-400 opacity-20 animate-ping"></span>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className={`fixed ${
          isHeaderPosition 
            ? "top-20 right-4" 
            : "bottom-24 right-6"
        } z-[10002] w-96 h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden animate-slide-up`}>
          {/* Chat Header */}
          <div className="bg-primary-500 dark:bg-primary-600 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <img
                src={robotGif}
                alt="Chatbot"
                className="w-8 h-8 object-contain"
              />
              <div>
                <h3 className="text-white font-semibold text-sm">Assistant</h3>
                <p className="text-white/80 text-xs">How can I help?</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-200 transition-colors p-1 rounded hover:bg-white/20"
              aria-label="Close chat"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Search Box */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for features..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Clear search"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Chat Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900/50 custom-scrollbar">
            {messages.map((message) => (
              <div key={message.id}>
                {message.type === "bot" && message.content && (
                  <div className="flex items-start gap-2 mb-3">
                    <img
                      src={robotGif}
                      alt="Chatbot"
                      className="w-7 h-7 object-contain flex-shrink-0"
                    />
                    <div className="flex-1">
                      <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2.5 shadow-sm border border-gray-200 dark:border-gray-700">
                        <p className="text-gray-800 dark:text-gray-100 text-sm leading-relaxed">
                          {message.content}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {message.type === "user" && message.content && (
                  <div className="flex items-start gap-2 mb-3 justify-end">
                    <div className="flex-1 flex justify-end">
                      <div className="bg-primary-500 dark:bg-primary-600 rounded-lg px-3 py-2.5 max-w-[80%]">
                        <p className="text-white text-sm">
                          {message.content}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {message.type === "help-options" && (
                  <div className="mb-3">
                    {searchQuery && filteredHelpOptions.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          No results found for "{searchQuery}"
                        </p>
                        <button
                          onClick={() => setSearchQuery("")}
                          className="mt-2 text-primary-500 dark:text-primary-400 text-sm hover:underline"
                        >
                          Clear search
                        </button>
                      </div>
                    )}
                    <div className="space-y-2">
                      {filteredHelpOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleHelpOptionClick(option)}
                          className="w-full text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-primary-500 dark:hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200 group"
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-2xl flex-shrink-0">{option.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                                  {option.title}
                                </h4>
                                {option.shortcut && (
                                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-[10px] text-gray-600 dark:text-gray-400 font-mono">
                                    {option.shortcut}
                                  </kbd>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                            <svg
                              className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5 group-hover:text-primary-500 dark:group-hover:text-primary-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Keyboard Shortcuts Button */}
                    <button
                      onClick={handleShowShortcuts}
                      className="w-full mt-3 p-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      View Keyboard Shortcuts
                    </button>
                  </div>
                )}

                {message.type === "shortcuts" && (
                  <div className="mb-3">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">Keyboard Shortcuts</h4>
                      <div className="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar">
                        {keyboardShortcuts.map((category, idx) => (
                          <div key={idx} className="mb-3">
                            <h5 className="text-xs font-semibold text-primary-600 dark:text-primary-400 mb-2 uppercase">
                              {category.category}
                            </h5>
                            <div className="space-y-1.5">
                              {category.shortcuts.map((shortcut, sIdx) => (
                                <div key={sIdx} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-600 dark:text-gray-400">{shortcut.action}</span>
                                  <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-800 dark:text-gray-200 font-mono">
                                    {shortcut.key}
                                  </kbd>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={handleBackToHelp}
                      className="w-full mt-2 p-2.5 bg-primary-500 dark:bg-primary-600 hover:bg-primary-600 dark:hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      ← Back to Help Options
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop when chat is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[10000]"
          onClick={() => setIsOpen(false)}
        />
      )}

      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        kbd {
          font-size: 10px;
          font-weight: 600;
        }
      `}</style>
    </>
  );
};
